import {ChartData} from "chart.js/auto";
import {ChartDataset} from "chart.js";
import {SalesDto} from "../../../models/sales/sales-dto";
import {ColorMapService} from "../../../../common/colors/color-map.service";
import {SalesWrapper} from "../../sales/sales-wrapper";
import {TimeIntervalBO} from "../../time/models/time-interval-bo";
import {LinkedDtoRepository} from "../../../../common/repositories/linked-dto-repository";
import {TimeIntervalHelper} from "../../time/time-interval-helper";


export class TimelineDatasetProcessor {
  static verifyDatasetContainsOnlyPositiveValues(dataset: ChartData): boolean {
    return dataset.datasets!.every((dataset) => dataset.data.every((x) => <number>x! >= 0));
  }

  static processBarChartDataset(groupKey: keyof SalesDto, salesWrapper: SalesWrapper, linkedDtoRepository: LinkedDtoRepository<any>, colorMapService: ColorMapService, timeIntervalBO: TimeIntervalBO, intervalDurationMultiple: number, revenueCalculationMethodFn: (salesDto: SalesDto) => number): ChartData {

    let data = this.buildRawTimelineBarChartData(salesWrapper.getBaseOrderElements(), (baseOrderElement) => baseOrderElement[groupKey].toString() as string, revenueCalculationMethodFn, timeIntervalBO, intervalDurationMultiple);
    TimelineDatasetProcessor.setColorOfBarChart(data, colorMapService, groupKey, salesWrapper);
    // Todo sort by revenue via quantativeBaseOrderCacheVO

    TimelineDatasetProcessor.sortDatasetsByTotalRevenue(data, salesWrapper, groupKey);
    TimelineDatasetProcessor.getLabelsOfBarChart(data, linkedDtoRepository, groupKey);
    return data
  }

  static sortDatasetsByTotalRevenue(data: ChartData, salesWrapper: SalesWrapper, groupKey: keyof SalesDto) {
    data.datasets = data.datasets.sort((a, b) => {
      let aTotalRevenue = salesWrapper.quantitativeBaseOrderCacheVO.dataCache[groupKey]!.find((element) => element.id === a.label)!.value;
      let bTotalRevenue = salesWrapper.quantitativeBaseOrderCacheVO.dataCache[groupKey]!.find((element) => element.id === b.label)!.value;
      return bTotalRevenue - aTotalRevenue;
    });
  }

  static getLabelsOfBarChart(data: ChartData, linkedDtoRepository: LinkedDtoRepository<any>, groupKey: keyof SalesDto) {
    data.datasets = data.datasets.map((dataset) => {
      dataset.label = linkedDtoRepository.getName(dataset.label!, groupKey);
      return dataset;
    });
  }

  static setColorOfBarChart(data: ChartData<"bar">, colorMapService: ColorMapService, groupKey: keyof SalesDto, salesWrapper: SalesWrapper) {
    let colorCount = data.datasets.length;

    for (let dataset of data.datasets!) {
      let index = salesWrapper.quantitativeBaseOrderCacheVO.dataCache[groupKey]!.findIndex((element) => element.id === dataset.label);
      dataset.backgroundColor = colorMapService.getColorAsRGB(index, colorCount, 0.5);
      dataset.borderColor = colorMapService.getColorAsRGB(index, colorCount);
    }
  }

  static buildRawTimelineBarChartData(salesDtoList: SalesDto[], getAttributeKeyFn: (salesDto: SalesDto) => string, dataValueFn: (salesDto: SalesDto) => number, timeIntervalBO: TimeIntervalBO, intervalDurationMultiple: number = 1): ChartData<"bar"> {
    let indexMap: { [key: string]: number } = {};
    let data: ChartDataset[] = [];
    for (let baseOrderElement of salesDtoList) {

      let index = indexMap[getAttributeKeyFn(baseOrderElement)] ?? -1;

      if (index === -1) {
        index = data.length;
        indexMap[getAttributeKeyFn(baseOrderElement)] = index;
        data.push({
          label: getAttributeKeyFn(baseOrderElement),
          data: [...new Array(timeIntervalBO.intervalCount).fill(0)],
          borderWidth: 1,

        });
      }

      let currentValue = data[index].data![baseOrderElement.timeInterval] as number;

      data[index].data![baseOrderElement.timeInterval] = currentValue + dataValueFn(baseOrderElement);
    }

    data = this.combineDataIntervals(data, intervalDurationMultiple, timeIntervalBO.intervalCount);

    // TODO: create Labels
    let labels = this.createTimeIntervalLabels(timeIntervalBO.roundedStartTime, timeIntervalBO.roundedEndTime, timeIntervalBO.intervalDuration, timeIntervalBO.intervalCount, intervalDurationMultiple);

    // Todo: apply color scheme

    //Todo: add properties with no value
    return {labels: labels, datasets: data} as ChartData<"bar">;
  }


  // ----------------- Helper Functions -----------------
  private static combineDataIntervals(data: ChartDataset[], intervalDurationMultiple: number, intervalCount: number): ChartDataset[] {
    let combinedIntervalCount = Math.ceil(intervalCount / intervalDurationMultiple)
    for (let _data of data) {
      for (let i = 0; i < combinedIntervalCount; i++) {
        let sum = 0;
        for (let j = 0; j < intervalDurationMultiple; j++) {
          sum += _data.data![i + j] as number;
        }
        _data.data![i] = sum;
        _data.data!.splice(i + 1, intervalDurationMultiple - 1);
      }
    }
    return data
  }

  private static createTimeIntervalLabels(startTime: Date, endTime: Date, intervalDuration: number, intervalCount: number, intervalDurationMultiple: number): string[] {
    let combinedIntervalCount = Math.ceil(intervalCount / intervalDurationMultiple)
    let labels: string[] = [];
    for (let i = 0; i < combinedIntervalCount; i++) {
      let intervalStartTime = startTime.getTime() + i * intervalDurationMultiple * intervalDuration;
      let intervalEndTime = Math.min(intervalStartTime + intervalDurationMultiple * intervalDuration, endTime.getTime());
      labels.push(TimeIntervalHelper.timespanToLabel(intervalStartTime, intervalEndTime));
    }
    return labels;
  }
}
